import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/react-hooks';

import {
  Typography,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';
import StarBorderRoundedIcon from '@material-ui/icons/StarBorderRounded';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

import TableSortLabel from '@material-ui/core/TableSortLabel';
import Pagination from 'components/pagination/Pagination';
import HandledCell from 'components/handled-status-cell/handledCell';

import BookingsDetailsDialogDialog from 'components/booking-details-dialog/BookingDetailsDialog';

import { ALL_BOOKINGS } from 'graphql/queries/bookings';
import useStyles from './styles';
import {
  getAccommodationBookingTotalInfo,
  getActivityBookingTotalInfo,
  guestsDetailsInformation,
  guestsSumSeats,
  isParticipantInfo
} from 'utils/bookings/prepareBookingsData';
import ListPopover from 'components/list-popover/ListPopover';
import NumberIcon from '../../../../components/number-icon/NumberIcon';
import { InfoOutlined } from '@material-ui/icons';
import { useSyncDialogContent } from '../../../../hooks/useSyncDialodContent';

const cancelledBookingsQueryVariablesConfig = (page, order, orderBy) => {
  return {
    all: {
      isCanceled: true,
      page: page,
      orderBy: order === 'asc' ? orderBy : `-${orderBy}`
    },
    handled: {
      isCanceled: true,
      isHandled: true,
      page: page,
      orderBy: order === 'asc' ? orderBy : `-${orderBy}`
    },
    notHandled: {
      isCanceled: true,
      isHandled: false,
      page: page,
      orderBy: order === 'asc' ? orderBy : `-${orderBy}`
    }
  };
};

const ACCOMMODATION_TYPE = 'Accommodation';
const ACTIVITY_TYPE = 'Activity';

const headingsConfig = classes => {
  return [
    {
      label: 'Reference • Date',
      align: 'left',
      sortBy: 'creation_time',
      cellClassName: classes.referenceCell
    },
    {
      label: 'Booking name',
      sortBy: 'guest_first_name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Accommodations/Activities',
      sortBy: 'title',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'NO. of guests',
      sortBy: 'guests',
      cellClassName: classes.guestsCell
    },
    {
      label: 'Platform',
      sortBy: 'platform',
      cellClassName: classes.platformCell,
      align: 'left'
    },
    {
      label: 'Cancelled date',
      cellClassName: classes.cancellationTimeCell,
      sortBy: 'cancellation_time',
      align: 'left'
    },
    {
      label: 'Notes',
      cellClassName: classes.notesCell,
      sortBy: '',
      align: 'left'
    },
    {
      label: 'Status',
      cellClassName: classes.statusCell,
      sortBy: '',
      align: 'right'
    }
  ];
};

function CancelledBookingsListTable({ handledStatus, businessOwner, search }) {
  const classes = useStyles();
  const [t] = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('creation_time');
  const [page, setPage] = React.useState(0);

  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const onClose = () => {
    setOpen(false);
  };

  const variables = {
    ...cancelledBookingsQueryVariablesConfig(page, order, orderBy)[
      handledStatus
    ],
    search: search || '',
    businessOwnerId:
      businessOwner.selectedBusinessOwner.ownerId ||
      businessOwner.selectedBusinessOwner.id
  };

  const { data, refetch } = useQuery(ALL_BOOKINGS, {
    variables
  });

  useSyncDialogContent(data, setDialogContent);

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  React.useEffect(() => {
    if (search === undefined) return;
    setPage(0);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
    refetch();
  }, [handledStatus, refetch]);

  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                style={{ paddingBottom: 15 }}
                className={classNames(classes.tableCell, heading.cellClassName)}
                sortDirection={orderBy === heading.label ? order : false}
              >
                <TableSortLabel
                  active={orderBy === heading.sortBy && orderBy !== ''}
                  direction={orderBy === heading.sortBy ? order : 'asc'}
                  onClick={createSortHandler(heading.sortBy)}
                  className={classes.heading}
                  disabled={heading.sortBy === ''}
                  hideSortIcon={heading.sortBy === ''}
                >
                  {t(heading.label)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.allBookings &&
            data.allBookings.map(booking => (
              <TableRow
                key={booking.id}
                classes={{ root: classes.tableRow }}
                onClick={e => {
                  if (booking.deactivationBooking) return;
                  e.stopPropagation();
                  openInfoDialog(booking);
                }}
                className={classNames({
                  [classes.disabledArea]: booking.deactivationBooking
                })}
              >
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.referenceCell
                  )}
                  align="left"
                >
                  <Typography>
                    {booking.trekksoftId || booking.expediaReferenceNumber
                      ? booking.trekksoftId || booking.expediaReferenceNumber
                      : booking.referenceNumber}
                  </Typography>
                  <Typography>{booking.creationTime}</Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  <Typography className={classes.nameCell}>
                    {booking.deactivationBooking
                      ? 'Deactivation Booking'
                      : `${booking.guestFirstName} ${booking.guestLastName}`}
                  </Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.unitCell)}
                  align="left"
                >
                  <div className={classes.unitContent}>
                    {booking.bookingType === ACTIVITY_TYPE && (
                      <StarBorderRoundedIcon
                        fontSize="small"
                        className={classes.unitIcon}
                      ></StarBorderRoundedIcon>
                    )}
                    {booking.bookingType === ACCOMMODATION_TYPE && (
                      <HomeOutlinedIcon
                        fontSize="small"
                        className={classes.unitIcon}
                      ></HomeOutlinedIcon>
                    )}
                    <Typography className={classes.title}>
                      {booking.bookingType === ACCOMMODATION_TYPE &&
                        booking.accommodation.title}

                      {booking.bookingType === ACTIVITY_TYPE &&
                        booking.activity.title}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.guestsCell)}
                  align="left"
                >
                  {booking.guests.length === 0 && `—`}
                  {booking.guests.length !== 0 && booking.guests.length <= 2 && (
                    <Typography className={classes.cellText}>
                      {guestsSumSeats(booking.guests)}
                      {isParticipantInfo(booking.guests) &&
                        ` (${guestsDetailsInformation(booking.guests, t)})`}
                    </Typography>
                  )}
                  {booking.guests.length !== 0 && booking.guests.length > 2 && (
                    <Typography className={classes.cellText}>
                      {guestsSumSeats(booking.guests)} (
                      {guestsDetailsInformation(booking.guests.slice(0, 2), t)}
                      ...
                    </Typography>
                  )}

                  {booking.guests.length !== 0 &&
                    booking.guests &&
                    booking.guests.length > 2 && (
                      <ListPopover
                        list={
                          <ul className={classes.list}>
                            {booking.guests.slice(2).map((guest, index) => {
                              return (
                                <li key={index}>
                                  {guest.guestsNumber} x{' '}
                                  {guest.participant[0].participant}
                                </li>
                              );
                            })}
                          </ul>
                        }
                      >
                        <NumberIcon number={booking.guests.length - 2} />
                      </ListPopover>
                    )}
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.platformCell
                  )}
                  align="left"
                >
                  {t(booking.platform)}
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.cancellationTimeCell
                  )}
                  align="left"
                >
                  <Typography>
                    {moment(booking.cancellationTime).format('DD-MM-YYYY')}
                  </Typography>
                  <Typography>
                    {moment(booking.cancellationTime).format('HH:mm')}
                  </Typography>
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5 }}
                  className={classNames(classes.tableCell, classes.notesCell)}
                  align="left"
                >
                  <ListPopover
                    maxWidth={300}
                    list={
                      <div className={classes.list} style={{ minWidth: 120 }}>
                        {booking.bookingNotes ||
                          booking.activityNotes ||
                          'There are no notes yet.'}
                      </div>
                    }
                  >
                    <InfoOutlined />
                  </ListPopover>
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.statusCell)}
                  align="right"
                >
                  <HandledCell
                    booking={booking}
                    refetch={refetch}
                    isHandledBooking={booking.isHandled}
                  />
                </TableCell>
              </TableRow>
            ))}
          <BookingsDetailsDialogDialog
            onClose={onClose}
            open={open}
            booking={dialogContent}
            bookingTotal={
              dialogContent.bookingType === ACTIVITY_TYPE
                ? getActivityBookingTotalInfo(dialogContent)
                : getAccommodationBookingTotalInfo(dialogContent)
            }
            refetchParams={{ query: ALL_BOOKINGS, variables }}
          />
        </TableBody>
      </Table>
      {data && data.allBookings && (
        <Pagination
          page={page + 1}
          count={data.numberOfBookingsPages.pages.allBookings}
          onChange={onPageChangeHandler}
          amount={`${page * 11 + 1}-${Math.min(
            (page + 1) * 11,
            data.numberOfBookingsPages.totalBookingsNumber.allBookings
          )}`}
          total={data.numberOfBookingsPages.totalBookingsNumber.allBookings}
        />
      )}
    </TableContainer>
  );
}

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(CancelledBookingsListTable);
